import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { listResidentComplaints } from "@/api/residents";
import type { TrustComplaint, TrustComplaintQueue } from "@/api/trust_complaints";
import {
  trustComplaintListTitle,
  trustComplaintQueueFor,
  trustComplaintSourceLabel,
  trustComplaintStatusLabel,
  trustComplaintStatusPillClass,
} from "@/components/trust_suite/trustSuiteDisplay";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { cn } from "@/lib/utils";

type ResidentConcernsListProps = {
  municipalityId: number;
  residentId: number;
  onOpenComplaint?: (complaintId: number, queue: TrustComplaintQueue) => void;
};

export function ResidentConcernsList({
  municipalityId,
  residentId,
  onOpenComplaint,
}: ResidentConcernsListProps) {
  const { data: complaints = [], isLoading, error } = useQuery({
    queryKey: ["resident_complaints", municipalityId, residentId],
    queryFn: () => listResidentComplaints(municipalityId, residentId) as Promise<TrustComplaint[]>,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading concerns…
      </div>
    );
  }

  if (error) {
    return <p className="py-4 text-sm text-destructive">Could not load concerns.</p>;
  }

  if (complaints.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No linked concerns yet.
      </p>
    );
  }

  return (
    <ul className="space-y-2" data-testid="resident-concerns-list">
      {complaints.map((complaint) => (
        <li key={complaint.id}>
          <button
            type="button"
            className="w-full rounded-lg border bg-background p-3 text-left transition-colors hover:bg-muted/40"
            onClick={() => onOpenComplaint?.(complaint.id, trustComplaintQueueFor(complaint))}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="line-clamp-2 text-sm font-medium">
                {trustComplaintListTitle(complaint)}
              </span>
              <span className="shrink-0 text-[10px] text-muted-foreground">#{complaint.id}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  "inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
                  trustComplaintStatusPillClass(complaint.status),
                )}
              >
                {trustComplaintStatusLabel(complaint.status)}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {trustComplaintSourceLabel(complaint.source)}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {formatRelativeTime(complaint.submitted_at)}
              </span>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
